import { ApiError } from '@discovr/common';
import type { ShopifyLifecycleState } from '@discovr/types';

/**
 * User-facing strings for the Shopify integration. Short,
 * action-first, past-tense confirmations. Same i18n-ready shape as appsflyerCopy.
 */
export const shopifyCopy = {
  claim: {
    // Shown on the sign-in code step while a Shopify install claim is pending in this tab.
    signInCodeHint: 'Enter the code here to finish connecting your Shopify store.',
    success: 'Shopify store connected',
    error: 'Could not connect your Shopify store',
    // Failures split by what the merchant can actually do about it. Telling a
    // brand_member to reinstall sends them round a loop they can never finish.
    previewError:
      "That connection link isn't valid any more. Open Discovr AI again from your Shopify admin (Apps → Discovr AI) to get a fresh connection link.",
    previewForbidden:
      'Only a brand owner or admin can connect a Shopify store. Ask one of them to install Discovr from your Shopify admin.',
    previewUnavailable: "We couldn't check that connection link just now. Try again.",
    claimUnavailable: "We couldn't connect the store just now. Try again.",
    claimBrandBusy:
      'Your brand already has a Shopify store connected. Uninstall Discovr from that store first (Integrations → Shopify → Manage), then install it on this one.',
    // Distinct from claimBrandBusy: this is a race on the brand-bound connect
    // path (InstallStart already refuses before Shopify is involved in the
    // normal case) where the shop turned out to be owned by a different
    // Discovr account. Names no account, and never says "connected" — the
    // caller's OWN install did not succeed.
    shopOwnedElsewhere:
      "That store is already connected to a different Discovr account. If it's yours, sign in to that account to manage it.",
    // Item 121 / the opened-from-Shopify hotfix. A normal app open on an
    // already-connected store is NOT an error, so this fires only when the
    // status call comes back explicitly not-connected — which means the store
    // belongs to a different Discovr account than the one signed in here.
    openedElsewhere:
      'This store is connected to a different Discovr account. Sign in to that account, or uninstall Discovr from the store and install again.',
    // ?shopify=expired (BE #661). Was falling into the generic "couldn't
    // connect" error, which told the merchant to retry the thing that just
    // expired instead of where to get a fresh link.
    installExpired:
      'Your connection link expired. Open Discovr AI again from your Shopify admin (Apps → Discovr AI) to finish connecting.',
    // Install finished, no claim token reached us, and the brand has NO store
    // connected. App-open obtains a fresh signed connection link.
    installFinishedNoToken:
      'Your Shopify install finished but the connection link was incomplete. Open Discovr AI again from your Shopify admin (Apps → Discovr AI), then confirm the store and Discovr account.',
    // Same cause, but a store IS already connected. `connected` only proves
    // the brand has SOME store (FindActiveByBrand), and the installed redirect
    // carries no shop, so we cannot tell "this store, re-installed" from "a
    // second store whose token we lost". Neither an error nor silence is
    // honest; this is true either way.
    installFinishedStoreConnected:
      "Your Shopify install finished. If the store you just installed isn't connected yet, open Discovr AI from its Shopify admin.",
    sessionExpired:
      'Your session expired. Sign in again, then open Discovr AI from your Shopify admin (Apps → Discovr AI).',
    declined:
      'No store was linked to this Discovr account. The app remains installed in Shopify. Open it from Shopify Apps to finish setup, or uninstall it in Shopify admin.',
    // The param-callback variant. Distinct from `declined` above, which the
    // dialog uses when the merchant backs out before any redirect happened.
    declinedOnCallback: 'You cancelled connecting Shopify.',
  },
  /** Installed in Shopify but never linked to a Discovr account. */
  unlinked: {
    badge: 'Not linked',
    body: (shopDomain?: string) =>
      `Discovr AI is installed${shopDomain ? ` on ${shopDomain}` : ' in your Shopify store'}, but it isn't linked to this Discovr account yet. Open Discovr AI from your Shopify admin, then confirm the store and account here.`,
    bodyExpired: (shopDomain?: string) =>
      `Discovr AI is installed${shopDomain ? ` on ${shopDomain}` : ' in your Shopify store'}, but its connection link has expired. Open Discovr AI from your Shopify admin to get a new one.`,
    ownerOnly: 'Only a brand owner or admin can link the store. Ask one of them to finish setup.',
    finish: 'Open Discovr AI in Shopify',
    uninstall: 'Uninstall in Shopify',
    uninstallHint:
      'To remove it, open Settings → Apps in your Shopify admin, find Discovr AI and choose Uninstall. Cancelling here never uninstalls it.',
    dismiss: 'Hide this',
    /** The connection link lasts 24 hours (BE ClaimTokenExpiry). */
    linkLifetimeMs: 24 * 60 * 60 * 1000,
  },
  /**
   * ONE campaign store-state vocabulary (item 131), keyed by the backend's
   * campaign `store_state`. Every campaign, report and share surface reads its
   * wording from here so a state is never called two different things.
   */
  storeFrozen:
    "Store unavailable in Shopify (frozen or closed). Codes can't be created or changed until Shopify reopens the store. Reports still work.",
  state: {
    store_unavailable: {
      title: 'Shopify store not reachable',
      badge: 'Store not reachable',
      body: "Discovr can't reach the Shopify store this campaign uses, so its codes are view only. Check the store in Integrations.",
      staffBody:
        "Discovr can't reach the Shopify store this campaign uses, so its codes are view only. The brand needs to check the store in its Integrations.",
      report: "New sales and referral visits won't appear while the store can't be reached.",
    },
    removed: {
      title: 'Shopify data removed after uninstall',
      badge: 'Data removed',
      body: "Discovr was uninstalled from this campaign's Shopify store, so its codes, sales and referral visits were deleted. Creator and content work continues.",
      publicBody:
        "The brand uninstalled Discovr from its Shopify store, so this campaign's codes and sales were deleted.",
    },
    not_connected: {
      title: 'No Shopify store connected',
      body: "This brand hasn't connected a Shopify store yet. Connect one from the brand Integrations tab.",
      staffBody:
        "This brand hasn't connected a Shopify store yet. The brand needs to connect one in its Integrations.",
      report: "No Shopify store is connected, so new sales won't appear.",
    },
  },
  /** RECONNECT1: setting a removed campaign up again on the store connected now. */
  setupAgain: {
    goToSettings: 'Go to campaign settings',
    available: (store: string) =>
      `You can set up Shopify again on ${store}. Tracking starts fresh; deleted data doesn't come back.`,
    connectStore: 'Connect a Shopify store in Integrations to set it up again.',
    lookupFailed: "Couldn't check whether Shopify can be set up again. Try again shortly.",
    reasons: {
      role: 'Only a brand owner or admin can set up Shopify again.',
      purge_pending:
        "Discovr is still deleting this campaign's previous Shopify data. Try again in a few minutes.",
    },
    dialog: {
      title: 'Set up Shopify again?',
      bullets: (store: string) => [
        `This campaign will use ${store}.`,
        'Previous codes, sales and referral visits stay deleted. Reports start from zero.',
        'Creators, approvals and content stay as they are.',
        'Creators who already posted have an old code in their content that no longer tracks. Generate gives them a new code to share.',
        'Next, choose the discount and generate codes in Shopify settings.',
      ],
      confirm: 'Set up again',
      confirming: 'Setting up…',
      cancel: 'Cancel',
      success: 'Shopify is set up again. Choose the discount to create new codes.',
      successRefresh: 'Shopify is set up again. Refresh to see the new setup.',
      errors: {
        SHOPIFY_SETUP_NOT_NEEDED: 'This campaign no longer needs Shopify set up again. Refresh to see its current state.',
        SHOPIFY_NOT_CONNECTED: 'Connect a Shopify store in Integrations, then try again.',
        SHOPIFY_UNINSTALL_IN_PROGRESS:
          'Discovr is being uninstalled from your store. Wait for it to finish, then try again.',
        SHOPIFY_STORE_UNAVAILABLE:
          "Discovr can't reach your Shopify store. Check the store in Integrations, then try again.",
        SHOPIFY_PURGE_PENDING:
          "Discovr is still deleting this campaign's previous Shopify data. Try again in a few minutes.",
        SHOPIFY_SETUP_STATE_CHANGED: 'Something changed while you were setting up. Refresh and try again.',
        CAMPAIGN_NOT_ACTIVE: 'Shopify can only be set up again on an active campaign.',
        FORBIDDEN: 'Only a brand owner or admin can set up Shopify again.',
      } as Record<string, string | undefined>,
      stillRemoved: 'Shopify is not set up yet. Refresh and try again.',
      fallback: "Couldn't set up Shopify again. Try again.",
    },
    history: (setUpOn: string, removedBefore: string) =>
      `Shopify set up again on ${setUpOn}. Data from before ${removedBefore} was deleted.`,
  },
  /** Campaign Settings → Integrations, the per-campaign Shopify switch (Q8):
   *  a feature switch for this campaign only, never an integration pause. */
  campaignSwitch: {
    label: 'Enable for this campaign',
    ariaLabel: 'Enable Shopify for this campaign',
    on: 'On for this campaign',
    off: 'Off for this campaign',
    connectToEnable: 'Connect your Shopify store in Integrations to turn this on',
    goToIntegrations: 'Go to Integrations',
    turnOffTitle: 'Turn off Shopify for this campaign?',
    turnOffBody: (codes: number) =>
      `${codes} active code${codes === 1 ? '' : 's'} will keep working in your store, but you won't be able to generate, edit or deactivate codes from this campaign until you turn Shopify back on for it.`,
    turnOff: 'Turn off',
    turningOff: 'Turning off…',
  },
  /** SW2-7: a write whose answer never arrived may still have happened. */
  writeUncertain: 'It may have gone through; refresh to check.',
  picker: {
    loadFailed: (what: 'products' | 'collections') => `Couldn’t load ${what} from Shopify. Try again.`,
    retry: 'Try again',
  },
  settings: {
    loadFailed: "Couldn't load Shopify settings.",
    refreshFailed: "Couldn't refresh Shopify settings. Showing what was last loaded.",
  },
  report: {
    exportCsv: 'Export CSV',
    exporting: 'Exporting…',
    exportFailed: "Couldn't export Shopify sales. Try again.",
    exportDone: (n: number) => `Exported ${n} ${n === 1 ? 'row' : 'rows'}.`,
    exportEmpty: 'No sales in this range to export.',
    exportEmptyAllTime: 'No sales to export yet.',
    exportRangeHint: 'Exports the selected range, in UTC days.',
    exportAllHint: 'Exports every sale to date.',
    loadFailed: "Couldn't load Shopify sales.",
    refreshFailed: (lastLoaded?: string) =>
      lastLoaded
        ? `Couldn't refresh Shopify sales. Showing what was last loaded at ${lastLoaded}.`
        : "Couldn't refresh Shopify sales. Showing what was last loaded.",
    retry: 'Try again',
    retrying: 'Retrying…',
    setup: {
      label: 'Setup progress',
      steps: {
        connected: 'Connected',
        codes: 'Codes created',
        visit: 'First visit',
        order: 'First order',
      },
      done: 'done',
      pending: 'pending',
      testOrder: 'Test order received, tracking works',
    },
    manageCodes: 'Manage codes',
    cancelledOrders: {
      label: (n: number) => `${n} cancelled`,
      tooltipLabel: 'About cancelled orders',
      hint: 'Orders cancelled in Shopify. Shown for context.',
    },
    liveNoVisits: (days: number) => `Live ${days} days, no visits yet`,
    discountsGiven: (amount: string) => `${amount} discounts given`,
    discountsPartial: {
      sub: 'Discounts partial',
      hint: (unknown: number) =>
        `${unknown === 1 ? '1 paid order has' : `${unknown} paid orders have`} no discount figure yet, so discounts given is not shown. It fills in as Discovr re-reads orders from Shopify. Orders older than 60 days stay unknown, because Shopify only returns the last 60 days.`,
      tooltipLabel: 'About net sales and discounts',
    },
    sinceLive: {
      header: 'Since live',
      hint: 'Net sales from orders placed after the creator\u2019s content went live. Per creator, so it repeats on each of their code rows. Within a date range, only that range counts.',
    },
    firstSale: {
      header: 'First sale',
      hint: 'How long after the content went live the first paid order came. Not limited by the date range.',
      after: (days: number) =>
        days === 0
          ? 'Same day as going live'
          : `${days} ${days === 1 ? 'day' : 'days'} after going live`,
    },
    help: {
      testOrder: 'How to place a test order',
      metrics: 'What do these numbers mean?',
      codes: 'How to create and test a code',
    },
    /** UX2: one explanation of where sales and visits come from, shared by
     *  every surface that shows them. */
    attribution:
      'Sales are attributed to the discount code used at checkout. If no Discovr code was used, a sale can be credited to the creator whose Discovr link the shopper arrived from. Referral visits are approximate and may be missing because of consent choices or browser blocking.',
    /** CONV-COPY1: the Conversions tab's "How attribution works" tooltip. The
     *  referral-visit caveat lives on the visits hint. */
    attributionHow: {
      triggerLabel: 'How attribution works',
      body: 'Sales are credited to the discount code used at checkout. If no Discovr code was used, the creator whose link the shopper arrived from gets the credit.',
    },
    paidOrders: {
      label: 'Paid orders',
      hint: "Paid orders placed with these codes. Shopify's own use count updates on its schedule and can differ.",
    },
    netSales: {
      label: 'Net sales',
      hint: "Paid orders, after discounts and refunds, before shipping and tax. Shopify's reports also include unpaid orders.",
    },
    visits: {
      label: 'Referral visits',
      header: 'Referral visits',
      hint: 'Approximate: recorded visits via discount links, repeats included. Consent choices and blockers can lower it.',
    },
    /** UX3: when Discovr stored the latest visit and applied the latest order
     *  update for this campaign's codes. Not when the shopper acted. */
    activity: {
      visit: 'Last visit',
      order: 'Last order update',
      visitNone: 'No referral visits yet',
      orderNone: 'No order updates yet',
      triggerLabel: 'About these times',
      hint: "When Discovr last stored a visit and last applied a Shopify order update. A quiet period doesn't mean tracking stopped.",
    },
    /** UX5: why a store_unavailable store is unavailable, when the BE knows. */
    lifecycle: {
      uninstalling: {
        badge: 'Uninstalling',
        note: "Discovr is being uninstalled from this campaign's Shopify store. Once it finishes, this campaign's Shopify codes, sales and referral visits are deleted.",
      },
      access_lost: {
        badge: 'Access lost',
        note: "Discovr can't reach this campaign's Shopify store right now. New sales are picked up once access returns; referral visits in the meantime aren't recorded. If this continues, reconnect from Shopify admin.",
      },
      deleting: {
        title: 'Deleting Shopify data',
        body: "Discovr was uninstalled from this campaign's Shopify store. Its Shopify codes, sales and referral visits are being deleted.",
      },
    },
  },
  // Shown only on an explicit sync_ok === false: the store is connected, so a
  // red error would overstate it, but sales silently not appearing is worse
  // than a note nobody reads.
  syncNotSetUp:
    "Store connected, but sync isn't set up. Sales may not appear. Reconnect from Shopify admin to fix.",
  /** Integrations card, by `lifecycle_state`. The store is named only while it
   *  is still connected / uninstalling / access lost; after that it is Shopify
   *  data and is never shown. */
  lifecycle: {
    connected: (shop: string) => `Connected to ${shop}`,
    uninstalling: (shop: string) => `Uninstalling Discovr from ${shop}…`,
    uninstallingNoShop: 'Uninstalling Discovr…',
    access_lost:
      "Discovr can't reach your Shopify store. Tracking is stopped. Open Discovr AI from Shopify admin to reconnect, or uninstall it there.",
    deleting: "Discovr was uninstalled. Deleting this store's Shopify data.",
    removed:
      "Discovr was uninstalled and this store's Shopify data was deleted. To connect a store, install Discovr AI from the Shopify App Store.",
    badge: {
      uninstalling: 'Uninstalling',
      access_lost: 'Access lost',
      deleting: 'Deleting data',
      removed: 'Removed',
    },
  },
  uninstall: {
    action: 'Uninstall Discovr from Shopify',
    ownerOnly: 'Uninstalling requires the brand owner',
    title: 'Uninstall Discovr from Shopify',
    description: (shop: string) => `This removes Discovr AI from ${shop}.`,
    consequences: [
      'Tracking stops and the Shopify data Discovr holds for this store is deleted.',
      'Creator and content work in your campaigns continues.',
      'Existing discount codes keep working in Shopify unless you deactivate them.',
      "Deleted data can't be recovered, so download your sales first.",
    ],
    downloadNote:
      "Download your Shopify sales first. Uninstalling deletes this store's sales history in Discovr.",
    downloadAll: 'Download CSV (all campaigns)',
    downloading: 'Downloading…',
    turnOffCodes: 'Turn off all Discovr discount codes in Shopify first',
    campaignsHeading: (n: number) => `Campaigns affected (${n})`,
    campaignsLoading: 'Checking which campaigns are affected…',
    campaignsError:
      "Couldn't load which campaigns are affected. Uninstalling still deletes the Shopify data for every campaign using this store.",
    creators: (n: number) => `${n} creator${n === 1 ? '' : 's'}`,
    // en-US grouping, as ShopifyConversionsTab's fmtInt, so the dialog and the report agree.
    counts: (c: { coupons: number; conversions: number; clicks: number }) => {
      const n = (v: number, one: string, many: string) => {
        const r = Math.round(v ?? 0);
        return `${new Intl.NumberFormat('en-US').format(r)} ${r === 1 ? one : many}`;
      };
      return `To be deleted: ${n(c.coupons, 'discount code', 'discount codes')}, ${n(c.conversions, 'conversion', 'conversions')}, ${n(c.clicks, 'referral visit', 'referral visits')}.`;
    },
    confirmPrefix: 'Write',
    confirmSuffix: 'to confirm',
    confirmWord: 'UNINSTALL',
    cancel: 'Cancel',
    submit: 'Uninstall',
    submitting: 'Uninstalling…',
    continueAnyway: 'Continue anyway',
    codeFailureLine: (
      name: string,
      codes: number,
      reason: UninstallFailureReason = 'shopify_error',
    ) =>
      reason === 'edit_in_progress'
        ? `${name}: ${codes} code${codes === 1 ? '' : 's'} being edited; deactivation not confirmed`
        : `${name}: deactivation not confirmed for ${codes} code${codes === 1 ? '' : 's'}`,
    unnamedCampaign: 'Campaign',
    codesTurnedOffNote: (n: number) =>
      `${n} code${n === 1 ? ' was' : 's were'} deactivated; reactivate them from the campaign if needed.`,
    success: 'Discovr uninstalled from Shopify',
    codesStillOn: (campaigns: number) =>
      `Deactivation could not be confirmed in ${campaigns} campaign${campaigns === 1 ? '' : 's'} in Shopify.`,
    errors: {
      confirmMismatch: "The confirmation text didn't match. Type UNINSTALL exactly.",
      inProgress: 'An uninstall is already in progress. Check back in a few minutes.',
      accessLost:
        "Discovr can't reach your Shopify store, so it can't uninstall from here. Uninstall Discovr AI from Shopify admin → Apps instead.",
      notConnected: 'No Shopify store is connected.',
      forbidden: 'Only the brand owner can uninstall Discovr from Shopify.',
      codesTurnOffFailed: (failedCampaigns: number) =>
        `Discovr couldn't deactivate the codes in ${failedCampaigns} campaign${failedCampaigns === 1 ? '' : 's'}. You can uninstall anyway or cancel.`,
      codesBeingEdited:
        'A discount code is being edited, so it was not deactivated here. Retry in a couple of minutes.',
      failed:
        "Discovr couldn't uninstall from your store. Try again, or remove it in Shopify admin → Apps.",
      revertFailed:
        "Discovr couldn't uninstall from your store. Wait a few minutes before trying again.",
      unavailable: "We couldn't uninstall just now. Try again.",
      stillWorking: 'Still working — checking status',
      unconfirmed: "We couldn't confirm the uninstall. Try again.",
    },
  },
  creatorHold: {
    badge: 'On hold',
    rowHint: 'This creator is on hold. Their code still works.',
    pickerNote: 'Gets a code once the hold is lifted',
  },
} as const;

/**
 * Pick the claim-preview message by what the caller can actually do about it.
 * Collapsing every failure into "reinstall" sent a brand_member round a loop
 * they can never finish, and told anyone hitting a 5xx to throw away a token
 * that was still valid.
 */
export function claimPreviewMessage(err: unknown): string {
  const status = err instanceof ApiError ? err.httpStatus : 0;
  if (err instanceof ApiError && err.code === 'shopify_already_claimed') {
    return shopifyCopy.claim.shopOwnedElsewhere;
  }
  if (status === 403) return shopifyCopy.claim.previewForbidden;
  // 404 consumed/unknown, 409 already claimed, 410 expired — all terminal for
  // this token, and only these genuinely call for a reinstall.
  if (status === 404 || status === 409 || status === 410) return shopifyCopy.claim.previewError;
  return shopifyCopy.claim.previewUnavailable;
}

/** A claim failure plus whether pressing the button again could ever help.
 *  `retryable` drives the button: offering "Try again" for a terminal error is
 *  worse than offering nothing, because it fails identically every time. */
export interface ClaimFailure {
  message: string;
  retryable: boolean;
}

/**
 * Claim failures need their own mapping, not the preview one. The statuses
 * overlap but the meanings do not: a 409 on claim is usually a
 * one-store refusal, where the link is perfectly valid and the fix is to
 * uninstall from the existing store — reinstalling does nothing.
 */
export function claimFailure(err: unknown): ClaimFailure {
  if (!(err instanceof ApiError)) {
    // Network drop or abort. The token is untouched, so another go is the
    // right advice.
    return { message: shopifyCopy.claim.claimUnavailable, retryable: true };
  }
  switch (err.httpStatus) {
    case 401:
      // Retrying cannot refresh a dead session, so offering "Try again" would
      // loop forever.
      return { message: shopifyCopy.claim.sessionExpired, retryable: false };
    case 403:
      return { message: shopifyCopy.claim.previewForbidden, retryable: false };
    case 409:
      if (err.code === 'shopify_already_claimed') {
        return { message: shopifyCopy.claim.shopOwnedElsewhere, retryable: false };
      }
      return err.code === 'shopify_uninstall_first'
        ? { message: shopifyCopy.claim.claimBrandBusy, retryable: false }
        : { message: shopifyCopy.claim.previewError, retryable: false };
    case 404:
    case 410:
      return { message: shopifyCopy.claim.previewError, retryable: false };
    default:
      return { message: shopifyCopy.claim.claimUnavailable, retryable: true };
  }
}

export type UninstallFailureKind =
  | 'confirm_mismatch'
  | 'in_progress'
  | 'access_lost'
  | 'not_connected'
  | 'forbidden'
  | 'codes_turn_off_failed'
  | 'failed'
  | 'still_working'
  | 'unconfirmed'
  | 'unavailable';

export type UninstallFailureReason = 'edit_in_progress' | 'shopify_error';

export interface UninstallFailure {
  kind: UninstallFailureKind;
  message: string;
  /** Codes already turned off before the failure (0 when none or unknown). */
  codesTurnedOff: number;
  /** Campaigns whose codes could not be turned off. */
  codeFailures: { campaignId: string; codeCount: number; reason: UninstallFailureReason }[];
}

const asRecord = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};

const asCount = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0;

function parseCodeFailures(raw: unknown): UninstallFailure['codeFailures'] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const f = asRecord(item);
    return typeof f.campaign_id === 'string'
      ? [
          {
            campaignId: f.campaign_id,
            codeCount: asCount(f.code_count),
            // An absent or unknown reason is not proof of an edit, so it reads as a Shopify error.
            reason: f.reason === 'edit_in_progress' ? 'edit_in_progress' : 'shopify_error',
          },
        ]
      : [];
  });
}

const NETWORK_ERROR_CODES: ReadonlySet<string> = new Set([
  'NETWORK_ERROR',
  'ECONNABORTED',
  'ERR_NETWORK',
  'ETIMEDOUT',
]);
const GATEWAY_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

type UninstallErrorClass = 'ambiguous' | 'unexpected' | 'api';

/** `ambiguous`: no answer from the backend (timeout, dropped connection, or a
 *  proxy 502/503/504 with no backend code), so the uninstall may have run. */
export function classifyUninstallError(err: unknown): UninstallErrorClass {
  if (err instanceof ApiError) {
    const proxyFailure = err.code === 'UNKNOWN' && GATEWAY_STATUSES.has(err.httpStatus);
    return err.httpStatus === 0 || proxyFailure ? 'ambiguous' : 'api';
  }
  const code = asRecord(err).code;
  return typeof code === 'string' && NETWORK_ERROR_CODES.has(code) ? 'ambiguous' : 'unexpected';
}

/**
 * Map an uninstall failure to what the dialog shows and which action it offers.
 * Keyed on the BE error code, not just the status: 409 alone covers three
 * different situations with three different next steps. `lifecycle` is the
 * refetched status: an ambiguous outcome is only "still working" while the
 * backend says so, otherwise it is retryable.
 */
export function uninstallFailure(
  err: unknown,
  lifecycle?: ShopifyLifecycleState,
): UninstallFailure {
  const copy = shopifyCopy.uninstall.errors;
  const base = { codesTurnedOff: 0, codeFailures: [] };
  const errorClass = classifyUninstallError(err);
  if (errorClass === 'ambiguous') {
    const inFlight = lifecycle === 'uninstalling' || lifecycle === 'deleting';
    return inFlight
      ? { ...base, kind: 'still_working', message: copy.stillWorking }
      : { ...base, kind: 'unconfirmed', message: copy.unconfirmed };
  }
  if (!(err instanceof ApiError)) {
    return { ...base, kind: 'unavailable', message: copy.unavailable };
  }
  const details = asRecord(err.details);
  const codesTurnedOff = asCount(details.codes_turned_off);
  switch (err.code) {
    case 'CONFIRM_MISMATCH':
      return { ...base, kind: 'confirm_mismatch', message: copy.confirmMismatch };
    case 'SHOPIFY_UNINSTALL_IN_PROGRESS':
      return { ...base, kind: 'in_progress', message: copy.inProgress };
    case 'SHOPIFY_ACCESS_LOST':
      return { ...base, kind: 'access_lost', message: copy.accessLost };
    case 'SHOPIFY_CODES_TURN_OFF_FAILED': {
      const codeFailures = parseCodeFailures(details.code_failures);
      // A turn-off that ran out of time left unlisted codes on too, so only a complete list can promise a quick retry.
      const onlyEdits =
        details.stopped_early !== true &&
        codeFailures.length > 0 &&
        codeFailures.every((f) => f.reason === 'edit_in_progress');
      return {
        kind: 'codes_turn_off_failed',
        message: onlyEdits ? copy.codesBeingEdited : copy.codesTurnOffFailed(codeFailures.length),
        codesTurnedOff,
        codeFailures,
      };
    }
    case 'SHOPIFY_UNINSTALL_FAILED': {
      const message = details.revert_failed === true ? copy.revertFailed : copy.failed;
      return { ...base, kind: 'failed', message, codesTurnedOff };
    }
  }
  if (err.httpStatus === 403) return { ...base, kind: 'forbidden', message: copy.forbidden };
  if (err.httpStatus === 404) return { ...base, kind: 'not_connected', message: copy.notConnected };
  return { ...base, kind: 'unavailable', message: copy.unavailable };
}
